"use client"

import { useState } from "react"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Plus, Trash2, Edit } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface Instruction {
  id: string
  title: string
  content: string
}

export function CustomInstructions() {
  const [savedInstructions, setSavedInstructions] = useState<Instruction[]>([
    {
      id: "1",
      title: "Greeting Protocol",
      content:
        "Always greet the caller with a warm and professional tone. Introduce yourself and ask how you can assist them today.",
    },
  ])
  const [editingInstructions, setEditingInstructions] = useState<Instruction[]>([])
  const [isEditing, setIsEditing] = useState(false)

  const addInstruction = () => {
    setIsEditing(true)
    const newInstruction: Instruction = {
      id: Date.now().toString(),
      title: "New Instruction",
      content: "",
    }
    setEditingInstructions([...editingInstructions, newInstruction])
  }

  const updateInstruction = (id: string, field: "title" | "content", value: string) => {
    setEditingInstructions(editingInstructions.map((inst) => (inst.id === id ? { ...inst, [field]: value } : inst)))
  }

  const deleteInstruction = (id: string) => {
    setEditingInstructions(editingInstructions.filter((inst) => inst.id !== id))
  }

  const deleteSavedInstruction = (id: string) => {
    setSavedInstructions(savedInstructions.filter((inst) => inst.id !== id))
  }

  const editSavedInstruction = (instruction: Instruction) => {
    setIsEditing(true)
    setEditingInstructions([instruction])
  }

  const handleSave = () => {
    if (editingInstructions.length > 0) {
      // Check if we're editing an existing instruction or adding new ones
      const updatedInstructions = [...savedInstructions]

      editingInstructions.forEach((editingInst) => {
        const existingIndex = updatedInstructions.findIndex((inst) => inst.id === editingInst.id)
        if (existingIndex >= 0) {
          updatedInstructions[existingIndex] = editingInst
        } else {
          updatedInstructions.push(editingInst)
        }
      })

      setSavedInstructions(updatedInstructions)
      setEditingInstructions([])
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setEditingInstructions([])
    setIsEditing(false)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Custom Instructions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Add specific instructions for your AI agent to follow during conversations
          </p>
        </div>
        <Button onClick={addInstruction} className="gap-2" disabled={isEditing}>
          <Plus className="h-4 w-4" />
          Add Instruction
        </Button>
      </div>

      {isEditing && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground">Editing Instructions</h3>
          {editingInstructions.map((instruction, index) => (
            <div key={instruction.id} className="border border-border rounded-lg p-4 space-y-3 bg-background">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <Label htmlFor={`title-${instruction.id}`} className="text-sm font-medium">
                    Instruction {index + 1} - Title
                  </Label>
                  <Input
                    id={`title-${instruction.id}`}
                    value={instruction.title}
                    onChange={(e) => updateInstruction(instruction.id, "title", e.target.value)}
                    placeholder="Enter instruction title..."
                    className="font-medium"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteInstruction(instruction.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`content-${instruction.id}`} className="text-sm font-medium">
                  Instruction Details
                </Label>
                <Textarea
                  id={`content-${instruction.id}`}
                  value={instruction.content}
                  onChange={(e) => updateInstruction(instruction.id, "content", e.target.value)}
                  className="min-h-[120px] font-mono text-sm"
                  placeholder="Enter detailed instructions..."
                />
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Instructions</Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Saved Instructions</h3>

        {savedInstructions.length > 0 ? (
          <div className="grid gap-4">
            {savedInstructions.map((instruction, index) => (
              <Card key={instruction.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base">
                        {index + 1}. {instruction.title}
                      </CardTitle>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => editSavedInstruction(instruction)}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteSavedInstruction(instruction.id)}
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{instruction.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <p className="text-muted-foreground mb-4">No instructions saved yet</p>
            <Button onClick={addInstruction} variant="outline" className="gap-2 bg-transparent">
              <Plus className="h-4 w-4" />
              Add Your First Instruction
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

